// src/hooks/useSlideshow.js
import { useState, useCallback } from "react";
import toast from "react-hot-toast";

export const useSlideshow = (apiUrl, userId) => {
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowNotes, setSlideshowNotes] = useState([]);
  const [slideshowJobName, setSlideshowJobName] = useState("");
  const [slideshowStartIndex, setSlideshowStartIndex] = useState(0);
  const [loadingSlideshowJob, setLoadingSlideshowJob] = useState(null);

  const handleOpenSlideshow = useCallback(
    async (item, startIndex = 0) => {
      console.log("=== handleOpenSlideshow called ===");
      console.log("Item received:", item);
      console.log("Item type check:", {
        hasTaskId: !!item.taskId,
        hasId: !!item.id,
        hasTitle: !!item.title,
        hasJobName: !!item.jobName,
        hasSiteNoteCount: !!item.siteNoteCount,
        itemKeys: Object.keys(item)
      });
      
      const isTask = item.id && 
                     item.title && 
                     !item.jobName && 
                     (item.status !== undefined || item.siteNoteCount !== undefined);
      
      const hasSiteNoteCount = item.siteNoteCount !== undefined;
      
      const definitelyIsTask = hasSiteNoteCount || isTask;
      
      console.log("Task detection result:", {
        isTask,
        hasSiteNoteCount,
        definitelyIsTask
      });
      
      let itemId, itemName;
      
      if (definitelyIsTask) {
        itemId = item.id;
        itemName = item.title;
        console.log("Processing as TASK with ID:", itemId, "Name:", itemName);
      } else {
        itemId = item.jobId || item.id || item.jobID;
        itemName = item.jobName || item.name;
        console.log("Processing as JOB with ID:", itemId, "Name:", itemName);
      }
      
      setLoadingSlideshowJob(itemId);
      
      try {
        if (item.notes && item.notes.length > 0) {
          console.log("Using cached notes:", item.notes.length);
          setSlideshowNotes(item.notes);
          setSlideshowJobName(definitelyIsTask ? `${itemName} - ${item.jobName || ''}` : itemName);
          setSlideshowStartIndex(startIndex);
          setSlideshowOpen(true);
          return;
        }
        
        const toastId = toast.loading(`Loading slideshow for ${itemName}...`);
        
        let url;
        
        if (definitelyIsTask) {
          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "1000",
            taskId: item.id.toString(),
            userId: userId?.toString() || "0"
          });
          
          url = `${apiUrl}/api/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
          
          console.log("Fetching TASK notes from URL:", url);
          
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch task notes: ${response.status}`);
          }
          
          const result = await response.json();
          console.log("API Response for TASK:", result);
          
          const allItems = result.data || [];
          
          console.log(`Found ${allItems.length} total items in response`);
          
          const noteItems = allItems.filter(item => item.itemType === "Note");
          console.log(`Filtered to ${noteItems.length} actual notes (excluded Task items)`);
          
          if (noteItems.length === 0) {
            toast.dismiss(toastId);
            toast.error(`No notes found for task: ${itemName}`);
            return;
          }
          
          // Format notes for slideshow
          const formattedNotes = noteItems.map((n) => ({
            id: n.id,
            note: n.note || n.content || n.description || '',
            content: n.note || n.content || n.description || '',
            userName: n.userName || "Unknown",
            userImageUrl: n.userImageUrl,
            date: n.date || n.createdAt,
            timeStamp: n.timeStamp || n.createdAt,
            taskName: n.title || item.title,
            jobName: n.job || item.jobName,
            projectName: n.project || item.projectName,
            workspaceName: n.workspace || item.workspaceName,
            title: n.title,
            description: n.description,
            taskId: n.taskId,
            friendlyId: n.friendlyId,
            status: n.status,
            priority: n.priority
          }));
          
          // Cache the notes on the item
          item.notes = formattedNotes;
          
          console.log(`Formatted ${formattedNotes.length} notes for slideshow`);
          
          setSlideshowNotes(formattedNotes);
          setSlideshowJobName(`${itemName} - ${item.jobName || 'Task Notes'}`);
          setSlideshowStartIndex(startIndex);
          setSlideshowOpen(true);
          
          toast.dismiss(toastId);
          toast.success(`Loaded ${formattedNotes.length} notes for this task`);
          
        } else {
          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "1000",
            jobId: itemId.toString(),
            userId: userId?.toString() || "0"
          });
          
          url = `${apiUrl}/api/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
          
          console.log("Fetching JOB notes from URL:", url);
          
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch job notes: ${response.status}`);
          }
          
          const result = await response.json();
          const allItems = result.data || [];
          
          const noteItems = allItems.filter(item => item.itemType === "Note");
          
          if (noteItems.length === 0) {
            toast.dismiss(toastId);
            toast.error(`No notes found for job: ${itemName}`);
            return;
          }
          
          const formattedNotes = noteItems.map((n) => ({
            id: n.id,
            note: n.note || n.content || '',
            content: n.note || n.content || '',
            userName: n.userName || "Unknown",
            userImageUrl: n.userImageUrl,
            date: n.date || n.createdAt,
            timeStamp: n.timeStamp || n.createdAt,
            taskName: n.title,
            jobName: n.job || item.jobName,
            projectName: n.project,
            workspaceName: n.workspace
          }));
          
          item.notes = formattedNotes;
          
          setSlideshowNotes(formattedNotes);
          setSlideshowJobName(item.jobName);
          setSlideshowStartIndex(startIndex);
          setSlideshowOpen(true);
          
          toast.dismiss(toastId);
        }
        
      } catch (error) {
        console.error("Error fetching notes for slideshow:", error);
        toast.error(`Failed to load notes: ${error.message}`);
      } finally {
        setLoadingSlideshowJob(null);
      }
    },
    [apiUrl, userId]
  );

  return {
    slideshowOpen,
    slideshowNotes,
    slideshowJobName,
    slideshowStartIndex,
    loadingSlideshowJob,
    setSlideshowOpen,
    handleOpenSlideshow,
  };
};